// NatalChartWheel - Stunning SVG natal chart visualization
// Custom-built for the Cosmic Compass aesthetic
import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZODIAC_SIGNS, PLANETS, ASPECTS, ELEMENTS, getPlanetByName } from '../../utils/astroConstants';
import {
    calculateChartDimensions,
    getZodiacSegments,
    getHouseLines,
    getPlanetPosition,
    getAspectLines,
    adjustPlanetPositions,
    longitudeToChartAngle
} from '../../utils/chartGeometry';

// SVG size
const SIZE = 400;
const CENTER = SIZE / 2;
const OUTER_RADIUS = SIZE / 2 - 20;
const ZODIAC_INNER = SIZE * 0.38;
const PLANET_RADIUS = SIZE * 0.28;
const HOUSE_LABEL_RADIUS = SIZE * 0.18;
const INNER_RADIUS = SIZE * 0.08;

export const NatalChartWheel = ({ chartData, onPlanetClick, selectedPlanet }) => {
    const [hoveredPlanet, setHoveredPlanet] = useState(null);
    const [showAspects, setShowAspects] = useState(true);

    const ascendant = chartData?.ascendant?.longitude || 0;
    const planets = chartData?.planets || [];
    const houseCusps = chartData?.houses?.map(h => h.cusp) || Array.from({ length: 12 }, (_, i) => i * 30);
    const aspects = chartData?.aspects || [];

    // Calculate zodiac segments
    const zodiacSegments = useMemo(() => {
        return getZodiacSegments(ascendant, CENTER, CENTER, ZODIAC_INNER, OUTER_RADIUS);
    }, [ascendant]);

    // Calculate house lines
    const houseLines = useMemo(() => {
        return getHouseLines(houseCusps, ascendant, CENTER, CENTER, INNER_RADIUS, ZODIAC_INNER);
    }, [houseCusps, ascendant]);

    // Calculate planet positions with collision avoidance
    const positionedPlanets = useMemo(() => {
        return adjustPlanetPositions(planets, ascendant, CENTER, CENTER, PLANET_RADIUS, 24);
    }, [planets, ascendant]);

    // Calculate aspect lines
    const aspectLines = useMemo(() => {
        if (!showAspects) return [];
        return getAspectLines(aspects, planets, ascendant, CENTER, CENTER, PLANET_RADIUS * 0.7);
    }, [aspects, planets, ascendant, showAspects]);

    // Get aspect color
    const getAspectColor = (aspectName) => {
        const aspect = ASPECTS.find(a => a.name === aspectName);
        return aspect?.color || '#666';
    };

    // Get element gradient for zodiac sign
    const getSignGradient = (index) => {
        const sign = ZODIAC_SIGNS[index];
        const element = ELEMENTS[sign.element];
        return element?.gradient || 'from-purple-500 to-violet-500';
    };

    // Get planet color
    const getPlanetColor = (name) => {
        const planet = PLANETS.find(p => p.name === name);
        return planet?.color || '#FFD700';
    };

    // Get planet glow
    const getPlanetGlow = (name) => {
        const planet = PLANETS.find(p => p.name === name);
        return planet?.glowColor || 'rgba(255, 215, 0, 0.5)';
    };

    return (
        <div className="relative w-full max-w-md mx-auto aspect-square">
            {/* Aspect toggle */}
            <button
                onClick={() => setShowAspects(!showAspects)}
                className="absolute top-2 right-2 z-10 px-3 py-1 text-xs bg-purple-900/50 backdrop-blur-sm rounded-full border border-purple-500/30 text-purple-200 hover:bg-purple-800/50 transition-colors"
            >
                {showAspects ? 'Hide Aspects' : 'Show Aspects'}
            </button>

            <svg
                viewBox={`0 0 ${SIZE} ${SIZE}`}
                className="w-full h-full"
                style={{ filter: 'drop-shadow(0 0 20px rgba(147, 51, 234, 0.3))' }}
            >
                {/* Definitions for gradients and filters */}
                <defs>
                    {/* Outer glow filter */}
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Planet glow filter */}
                    <filter id="planetGlow" x="-100%" y="-100%" width="300%" height="300%">
                        <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Element gradients */}
                    <linearGradient id="fireGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#FF6B6B" />
                        <stop offset="100%" stopColor="#FF9F43" />
                    </linearGradient>
                    <linearGradient id="earthGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#4CAF50" />
                        <stop offset="100%" stopColor="#8BC34A" />
                    </linearGradient>
                    <linearGradient id="airGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#64B5F6" />
                        <stop offset="100%" stopColor="#E1BEE7" />
                    </linearGradient>
                    <linearGradient id="waterGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#6C5CE7" />
                        <stop offset="100%" stopColor="#00CED1" />
                    </linearGradient>

                    {/* Center radial gradient */}
                    <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="rgba(147, 51, 234, 0.3)" />
                        <stop offset="100%" stopColor="transparent" />
                    </radialGradient>
                </defs>

                {/* Background circle with cosmic gradient */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={OUTER_RADIUS + 10}
                    fill="url(#centerGlow)"
                    opacity={0.5}
                />

                {/* Outer ring border */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={OUTER_RADIUS}
                    fill="none"
                    stroke="rgba(255, 215, 0, 0.3)"
                    strokeWidth="2"
                    filter="url(#glow)"
                />

                {/* Zodiac segments */}
                {zodiacSegments.map((segment, i) => {
                    const sign = ZODIAC_SIGNS[i];
                    const gradientId = `${sign.element.toLowerCase()}Gradient`;

                    return (
                        <g key={`zodiac-${i}`}>
                            {/* Segment background */}
                            <path
                                d={segment.path}
                                fill={`url(#${gradientId})`}
                                fillOpacity={0.15}
                                stroke="rgba(255, 255, 255, 0.2)"
                                strokeWidth="1"
                            />

                            {/* Zodiac symbol */}
                            <text
                                x={segment.labelX}
                                y={segment.labelY}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize="18"
                                fill={ZODIAC_SIGNS[i].color}
                                className="select-none"
                                style={{ textShadow: `0 0 10px ${ZODIAC_SIGNS[i].color}` }}
                            >
                                {sign.symbol}
                            </text>
                        </g>
                    );
                })}

                {/* Inner zodiac border */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={ZODIAC_INNER}
                    fill="rgba(10, 10, 30, 0.7)"
                    stroke="rgba(255, 215, 0, 0.2)"
                    strokeWidth="1"
                />

                {/* House division lines */}
                {houseLines.map((line, i) => (
                    <line
                        key={`house-line-${i}`}
                        x1={line.x1}
                        y1={line.y1}
                        x2={line.x2}
                        y2={line.y2}
                        stroke={i === 0 ? 'rgba(255, 215, 0, 0.6)' : 'rgba(255, 255, 255, 0.2)'}
                        strokeWidth={i === 0 || i === 3 || i === 6 || i === 9 ? 2 : 1}
                        strokeDasharray={i === 0 ? 'none' : '4,4'}
                    />
                ))}

                {/* House numbers */}
                {houseLines.map((line, i) => {
                    const nextCusp = houseCusps[(i + 1) % 12];
                    let midAngle = (houseCusps[i] + nextCusp) / 2;
                    if (nextCusp < houseCusps[i]) midAngle = ((houseCusps[i] + nextCusp + 360) / 2) % 360;

                    const chartAngle = longitudeToChartAngle(midAngle, ascendant);
                    const labelAngle = (chartAngle - 90) * (Math.PI / 180);
                    const labelX = CENTER + HOUSE_LABEL_RADIUS * Math.cos(labelAngle);
                    const labelY = CENTER + HOUSE_LABEL_RADIUS * Math.sin(labelAngle);

                    return (
                        <text
                            key={`house-num-${i}`}
                            x={labelX}
                            y={labelY}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="10"
                            fill="rgba(255, 215, 0, 0.5)"
                            className="select-none"
                        >
                            {i + 1}
                        </text>
                    );
                })}

                {/* Aspect lines */}
                <AnimatePresence>
                    {showAspects && aspectLines.map((aspect, i) => (
                        <motion.line
                            key={`aspect-${i}`}
                            x1={aspect.x1}
                            y1={aspect.y1}
                            x2={aspect.x2}
                            y2={aspect.y2}
                            stroke={getAspectColor(aspect.aspect)}
                            strokeWidth={aspect.isExact ? 2 : 1}
                            strokeOpacity={0.4}
                            strokeDasharray={aspect.aspect === 'Opposition' ? '6,3' : aspect.aspect === 'Square' ? '3,3' : 'none'}
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 0.4 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5, delay: i * 0.05 }}
                        />
                    ))}
                </AnimatePresence>

                {/* Inner circle */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={INNER_RADIUS}
                    fill="rgba(10, 10, 30, 0.9)"
                    stroke="rgba(147, 51, 234, 0.5)"
                    strokeWidth="2"
                />

                {/* Center decoration */}
                <circle
                    cx={CENTER}
                    cy={CENTER}
                    r={INNER_RADIUS - 5}
                    fill="none"
                    stroke="rgba(255, 215, 0, 0.3)"
                    strokeWidth="1"
                />

                {/* Planets */}
                {positionedPlanets.map((planet, i) => {
                    const isHovered = hoveredPlanet === planet.name;
                    const isSelected = selectedPlanet === planet.name;
                    const planetMeta = getPlanetByName(planet.name);

                    return (
                        <motion.g
                            key={`planet-${planet.name}`}
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: i * 0.1, type: 'spring', stiffness: 200 }}
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredPlanet(planet.name)}
                            onMouseLeave={() => setHoveredPlanet(null)}
                            onClick={() => onPlanetClick?.(planet)}
                        >
                            {/* Planet glow circle */}
                            <circle
                                cx={planet.displayX}
                                cy={planet.displayY}
                                r={isHovered || isSelected ? 18 : 14}
                                fill={getPlanetGlow(planet.name)}
                                opacity={isHovered || isSelected ? 0.8 : 0.4}
                                style={{ transition: 'all 0.2s ease' }}
                            />

                            {/* Planet background */}
                            <circle
                                cx={planet.displayX}
                                cy={planet.displayY}
                                r={12}
                                fill="rgba(20, 20, 40, 0.9)"
                                stroke={getPlanetColor(planet.name)}
                                strokeWidth={isSelected ? 3 : 2}
                                filter="url(#planetGlow)"
                            />

                            {/* Planet symbol */}
                            <text
                                x={planet.displayX}
                                y={planet.displayY}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize="14"
                                fill={getPlanetColor(planet.name)}
                                className="select-none font-bold"
                                style={{ textShadow: `0 0 8px ${getPlanetGlow(planet.name)}` }}
                            >
                                {planet.symbol}
                            </text>
                        </motion.g>
                    );
                })}

                {/* Ascendant marker */}
                <g>
                    <text
                        x={20}
                        y={CENTER}
                        textAnchor="start"
                        dominantBaseline="central"
                        fontSize="12"
                        fill="#FFD700"
                        fontWeight="bold"
                        className="select-none"
                    >
                        ASC
                    </text>
                </g>
            </svg>

            {/* Hover tooltip */}
            <AnimatePresence>
                {hoveredPlanet && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-purple-900/90 backdrop-blur-sm rounded-lg border border-purple-500/30 text-center"
                    >
                        {(() => {
                            const planet = planets.find(p => p.name === hoveredPlanet);
                            if (!planet) return null;
                            return (
                                <>
                                    <div className="text-amber-300 font-semibold">
                                        {planet.symbol} {planet.name}
                                    </div>
                                    <div className="text-purple-200 text-sm">
                                        {planet.formattedPosition} • House {planet.house}
                                    </div>
                                </>
                            );
                        })()}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default NatalChartWheel;
